import {useCallback, useEffect, useState} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const ADDRESS_NOW_BASE_URL = 'https://api.addressnow.co.uk'
const ADDRESS_NOW_VERSION = '2.20'

const ROYAL_MAIL_KEY_TYPES = {
    ACCOUNT_ADDRESS_BOOK: 'accountAddressBook',
    CHECKOUT: 'checkout'
}

const buildAddressNowAssetId = ({assetType, keyType}) =>
    `royal-mail-addressnow-${keyType}-${assetType}`

const isBrowser = typeof window !== 'undefined'

const getRoyalMailAddressNowKey = (appConfig, keyType) => {
    if (keyType === ROYAL_MAIL_KEY_TYPES.CHECKOUT) {
        return appConfig.ROYAL_MAIL_ADDRESSNOW_CHECKOUT_KEY
    }

    return appConfig.ROYAL_MAIL_ADDRESSNOW_ACCOUNT_ADDRESS_BOOK_KEY
}

const isConfigFlagEnabled = (value) => value === true || value === 'true'

const buildAddressNowAssetUrl = ({baseUrl, assetType, version, key}) => {
    const normalizedBaseUrl = baseUrl.replace(/\/$/, '')
    const extension = assetType === 'css' ? 'css' : 'js'

    return `${normalizedBaseUrl}/${assetType}/addressnow-${version}.min.${extension}?key=${encodeURIComponent(
        key
    )}`
}

const loadStylesheet = ({baseUrl, version, key, keyType}) => {
    if (!isBrowser) return Promise.resolve()

    const stylesheetId = buildAddressNowAssetId({assetType: 'css', keyType})
    const existingLink = document.getElementById(stylesheetId)

    if (existingLink) {
        return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
        const link = document.createElement('link')

        link.id = stylesheetId
        link.rel = 'stylesheet'
        link.type = 'text/css'
        link.href = buildAddressNowAssetUrl({
            baseUrl,
            assetType: 'css',
            version,
            key
        })

        link.onload = resolve
        link.onerror = () => reject(new Error('Failed to load Royal Mail AddressNow CSS.'))

        document.head.appendChild(link)
    })
}

const loadScript = ({baseUrl, version, key, keyType}) => {
    if (!isBrowser) return Promise.resolve()

    const scriptId = buildAddressNowAssetId({assetType: 'script', keyType})
    const existingScript = document.getElementById(scriptId)

    if (existingScript) {
        return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script')

        script.id = scriptId
        script.type = 'text/javascript'
        script.src = buildAddressNowAssetUrl({
            baseUrl,
            assetType: 'js',
            version,
            key
        })

        script.onload = resolve
        script.onerror = () => reject(new Error('Failed to load Royal Mail AddressNow script.'))

        document.head.appendChild(script)
    })
}

const normalizeRoyalMailAddress = (address = {}) => {
    const address1 = address.Line1 || ''
    const address2 = [address.Line2, address.Line3, address.Line4].filter(Boolean).join(' ')
    const city = address.City || address.PostTown || ''
    const postalCode = address.Postcode || address.PostalCode || ''
    const stateCode = address.Province || address.County || ''

    return {
        address1,
        address2,
        city,
        postalCode,
        stateCode,
        raw: address
    }
}

export default function useRoyalMailAddressAutocomplete({
    countryCode,
    enabled = true,
    keyType = ROYAL_MAIL_KEY_TYPES.ACCOUNT_ADDRESS_BOOK,
    onAddressSelected
} = {}) {
    const {app: appConfig} = getConfig()

    const [isLoaded, setIsLoaded] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    const key = getRoyalMailAddressNowKey(appConfig, keyType)
    const baseUrl = appConfig.ROYAL_MAIL_ADDRESSNOW_BASE_URL || ADDRESS_NOW_BASE_URL
    const version = appConfig.ROYAL_MAIL_ADDRESSNOW_VERSION || ADDRESS_NOW_VERSION

    const isActive =
        isConfigFlagEnabled(appConfig.ROYAL_MAIL_ADDRESSNOW_ENABLED) &&
        enabled &&
        countryCode === 'GB'

    const disable = useCallback(() => {
        if (!isBrowser || !window.addressNow?.disable) return

        window.addressNow.disable()
    }, [])

    const loadAddressNowWhenFieldIsReady = useCallback((attempt = 0) => {
        if (!isBrowser || !window.addressNow?.load) return

        const address1 = document.querySelector('#address1')

        if (address1 && address1.offsetParent) {
            window.addressNow.disable?.()
            window.addressNow.load()
            return
        }

        if (attempt < 10) {
            window.requestAnimationFrame(() => loadAddressNowWhenFieldIsReady(attempt + 1))
        }
    }, [])

    const load = useCallback(async () => {
        if (!isActive) {
            disable()
            setIsLoaded(false)
            return
        }

        if (!key) {
            setError(
                new Error(
                    `Missing Royal Mail AddressNow key configuration for key type "${keyType}".`
                )
            )
            setIsLoaded(false)
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            await loadStylesheet({baseUrl, version, key, keyType})
            await loadScript({baseUrl, version, key, keyType})

            if (!window.addressNow) {
                throw new Error('Royal Mail AddressNow did not initialize.')
            }

            window.addressNow.listen('options', (options) => {
                options.bar = options.bar || {}
                options.bar.visible = false

                options.search = {
                    maxSuggestions: 7,
                    maxResults: 100
                }
            })

            window.addressNow.listen('load', (control) => {
                control.listen('populate', (address) => {
                    if (typeof onAddressSelected === 'function') {
                        onAddressSelected(normalizeRoyalMailAddress(address))
                    }
                })
            })

            loadAddressNowWhenFieldIsReady()

            setIsLoaded(true)
        } catch (e) {
            setError(e)
            setIsLoaded(false)
        } finally {
            setIsLoading(false)
        }
    }, [
        baseUrl,
        disable,
        isActive,
        key,
        keyType,
        loadAddressNowWhenFieldIsReady,
        onAddressSelected,
        version
    ])

    useEffect(() => {
        load()

        return () => {
            disable()
        }
    }, [disable, load])

    return {
        isLoaded,
        isLoading,
        error,
        load,
        disable
    }
}